/*
  Demo review engine.

  Reviews are generated deterministically from a seeded PRNG rather than shipped
  as a JSON blob -- 5,000 review objects as data would be roughly 750KB over the
  wire, while the generator plus its phrase pools is a few KB and produces the
  same result on every load, so dates and "most helpful" order stay stable
  between page views and between visitors.

  Ratings run 3 to 5 only, so the copy pools carry no negative tone.
*/
(function () {
  'use strict';

  // Bump when changing submit behaviour, so a stale asset is identifiable.
  var PR_BUILD = 'captcha-iframe-6';

  /* ---------------------------------------------------------------- pools */

  var FIRST_NAMES = [
    'Aarav', 'Aditi', 'Akash', 'Ananya', 'Ankit', 'Anjali', 'Arjun', 'Asha', 'Bhavna', 'Chirag',
    'Deepa', 'Devang', 'Divya', 'Farhan', 'Gaurav', 'Geeta', 'Harsh', 'Ishaan', 'Jaya', 'Kabir',
    'Kavya', 'Kiran', 'Lakshmi', 'Manish', 'Meera', 'Mohit', 'Naveen', 'Neha', 'Nikhil', 'Pallavi',
    'Pooja', 'Pranav', 'Priya', 'Rahul', 'Rajesh', 'Ramya', 'Ravi', 'Riya', 'Rohan', 'Sanjay',
    'Sameer', 'Shalini', 'Shreya', 'Siddharth', 'Simran', 'Sneha', 'Sunil', 'Swati', 'Tanvi', 'Tarun',
    'Uma', 'Varun', 'Vikram', 'Vinay', 'Vishal', 'Yash', 'Zoya', 'Nandini', 'Karthik', 'Lavanya'
  ];

  var LAST_NAMES = [
    'Sharma', 'Verma', 'Patel', 'Reddy', 'Nair', 'Iyer', 'Gupta', 'Singh', 'Kumar', 'Mehta',
    'Joshi', 'Desai', 'Chopra', 'Bose', 'Rao', 'Menon', 'Kulkarni', 'Bansal', 'Agarwal', 'Pillai',
    'Chauhan', 'Sinha', 'Dutta', 'Ghosh', 'Malhotra', 'Shetty', 'Bhat', 'Naidu', 'Saxena', 'Trivedi'
  ];

  /*
    Bucketed by rating. Bodies are assembled from two independent pools so the
    combinations do not repeat quickly across 5,000 rows: roughly 20 x 20 = 400
    body variants per bucket before names, dates and titles are layered on.
  */
  var COPY = {
    great: {
      titles: [
        'Genuinely surprised by the results', 'Best plant purchase this year', 'My plants have never looked better',
        'Worth every rupee', 'Visible difference in two weeks', 'Exactly what my garden needed',
        'Great product at a great price', 'Repeat buyer, no complaints', 'Finally something that works',
        'Balcony garden completely transformed', 'Highly recommend this combo', 'Better than what I used before',
        'Delivered fast and works well', 'My money plant is thriving', 'New growth everywhere',
        'Simple to use, real results', 'Excellent value for the quantity', 'Will definitely reorder',
        'Impressed with the packaging too', 'Tired plants completely revived', 'Perfect for beginners',
        'Consistent results across all my pots', 'No more yellowing leaves', 'Strong, healthy foliage now',
        'Five stars from a sceptic', 'Bought again within a month', 'Even my curry leaf plant recovered',
        'Great for indoor plants', 'Noticeable within days', 'Solid product, honest pricing'
      ],
      bodies1: [
        'I have been using this for about six weeks now and the difference is obvious.',
        'Started applying it fortnightly and my plants responded almost immediately.',
        'Was quite sceptical at first because I have tried a lot of these products.',
        'Ordered this for my balcony garden and I am glad I did.',
        'My tulsi and money plant were struggling badly before this.',
        'Bought it after a friend recommended it and it lived up to the promise.',
        'The instructions are clear and it takes barely a minute to apply.',
        'I have around fifteen pots on my terrace and used this on all of them.',
        'Works better than many expensive brands I have tried.',
        'Delivery was quick and the packaging was leak proof, which I appreciate.',
        'This is my third order, which should say enough.',
        'I mostly grow herbs and this has worked beautifully for them.',
        'My hibiscus had stopped flowering completely for almost a year.',
        'Used it on both indoor and outdoor plants to compare.',
        'Honestly did not expect much at this price point.',
        'Picked this up during a sale and it turned out to be the best thing I bought.',
        'My mother has been gardening for thirty years and even she was impressed.',
        'Have recommended this to three people in my building already.',
        'The combo covers everything, so I did not need to buy anything separately.',
        'I water twice a week and added this to the routine without any trouble.'
      ],
      bodies2: [
        'New leaves are coming in thicker and the colour is a much deeper green.',
        'There is fresh growth at every node and the older leaves have stopped dropping.',
        'The plants look fuller and the stems are visibly sturdier than before.',
        'Flowering picked up within three weeks and has not stopped since.',
        'No burning or damage at all, which was my main worry with fertilisers.',
        'The quantity lasts a long time so the cost per use works out very low.',
        'Even the plants I had almost given up on have started putting out shoots.',
        'My neighbours have started asking what I am using.',
        'Would recommend it to anyone who is just starting out with plants.',
        'It has become a permanent part of my monthly plant routine.',
        'The leaves are glossier and there is much less yellowing overall.',
        'Root growth seems stronger too, which I noticed while repotting.',
        'Consistent results, no surprises, exactly what I wanted.',
        'Good value considering how long one pack lasts.',
        'Will be ordering the larger size next time.',
        'No odour at all, which matters to me since these are indoor plants.',
        'Two months in and the growth has kept up rather than tapering off.',
        'Honestly better than the nursery mix I was paying more for.',
        'Easy to store and the pack has not clumped up at all.',
        'My only regret is not buying it sooner.'
      ]
    },
    good: {
      titles: [
        'Good, but takes a little time', 'Works, just slower than expected', 'Decent product overall',
        'Happy with the purchase', 'Does what it says', 'Solid, if not miraculous',
        'Good for the price', 'Works better on some plants', 'Reasonable results',
        'Would buy again', 'No complaints', 'Better than average',
        'Steady improvement', 'Pretty good value', 'Does the job well',
        'Satisfied overall', 'Good quality, gradual results', 'Worth trying'
      ],
      bodies1: [
        'It does work, though not quite as dramatically as some reviews suggest.',
        'Results took closer to a month for me rather than the two weeks mentioned.',
        'Fine product, though my expectations were probably a little high going in.',
        'Worked well on my succulents while the ferns took longer to respond.',
        'The product itself is good, delivery took a couple of days longer than promised.',
        'Reasonable quality for what you pay.',
        'Used it through one full season on about eight pots.',
        'Bought this to replace a more expensive brand I was using.',
        'Applied it exactly as directed, once a fortnight.',
        'Nice product, I just wish the instructions had a bit more detail.',
        'Second time ordering, so clearly it is doing something right.',
        'My indoor plants responded faster than the ones on the terrace.',
        'The pack is smaller than I pictured but it goes a long way.',
        'Started seeing changes around the third week.',
        'Ordered mainly for my flowering plants.',
        'Straightforward to use and it mixes well.',
        'Have been using it alongside my usual compost.',
        'Got this on a recommendation from a gardening group.',
        'Simple product, no fuss.',
        'Tried it on a few pots first before using it everywhere.'
      ],
      bodies2: [
        'Still, the improvement is real and I would buy it again.',
        'Nothing wrong with it, just be patient with the results.',
        'Four stars because the results are genuine, just gradual.',
        'I would recommend it, with the caveat that it is not overnight.',
        'Packaging could be slightly better but the product itself is fine.',
        'Overall satisfied and will probably reorder.',
        'Leaves look healthier and there is definitely new growth.',
        'Good value, and it lasted longer than I expected.',
        'Does what it claims, just give it a few weeks.',
        'Would suggest it to anyone with a small balcony setup.',
        'A solid option if you want something reliable rather than dramatic.',
        'Happy enough to keep it in the rotation.',
        'The colour of the foliage improved noticeably.',
        'No issues at all, works as described.',
        'Slightly slower than I hoped but the results held.',
        'Reasonably priced for the quantity you get.',
        'I would call it dependable rather than spectacular.',
        'Fine product, easy to recommend.',
        'Will finish this pack and order again.',
        'Nothing to complain about here.'
      ]
    },
    average: {
      titles: [
        'Okay, does the basics', 'Average but usable', 'Middling results for me',
        'Fine, nothing special', 'It works, slowly', 'Reasonable but not remarkable',
        'Mixed results', 'Alright for the price', 'Neither great nor bad',
        'Took a while to show anything', 'Acceptable', 'Does the minimum well'
      ],
      bodies1: [
        'Used it for two months and the change was modest rather than dramatic.',
        'Some of my plants responded well, others did not change much at all.',
        'The product arrived in good condition and applying it is easy enough.',
        'Not a huge difference compared to what I was using earlier.',
        'It is fine, though I expected a bit more given the reviews.',
        'Worked on my flowering plants but I saw little change in the leafy ones.',
        'Applied it through a full season on a mixed set of pots.',
        'No problems with the product, the results were just gradual.',
        'Possibly my soil is the variable here rather than the product.',
        'Took about six weeks before I noticed anything at all.',
        'Bought the combo hoping for a bigger difference.',
        'Reasonable product, average outcome in my case.'
      ],
      bodies2: [
        'Giving it three stars since the packaging and delivery were both good.',
        'I would call it average, though nothing about it is actually bad.',
        'Might work better for someone with a different setup than mine.',
        'Support was responsive when I wrote in, which counts for something.',
        'Would consider trying it again with better soil prep.',
        'Fine for the price, just do not expect a transformation.',
        'Some improvement in the newer leaves, not much elsewhere.',
        'It is a reasonable option, just not a standout one.',
        'Happy enough to finish the pack.',
        'Middle of the road, which is roughly what I paid for.',
        'No complaints about quality, just modest results.',
        'Worth a try if you are already ordering something else.'
      ]
    }
  };

  /* ------------------------------------------------------------ generator */

  /*
    FNV-1a. Folded into the configured seed so every SKU generates its own
    distinct set of reviews from the same settings -- same product always gives
    the same reviews, different products never share them.
  */
  function hashString(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  // mulberry32 -- small, fast, and repeatable across browsers.
  function rng(seed) {
    var t = seed >>> 0;
    return function () {
      t = (t + 0x6d2b79f5) >>> 0;
      var r = Math.imul(t ^ (t >>> 15), 1 | t);
      r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  function pick(rand, arr) {
    return arr[Math.floor(rand() * arr.length) % arr.length];
  }

  function buildReviews(config) {
    // Per-SKU seed: the configured seed mixed with the product handle.
    var seed = (config.seed ^ hashString(config.productHandle || '')) >>> 0;
    var rand = rng(seed);
    var counts = config.distribution; // index 0 = 1 star ... index 4 = 5 star
    var stars = [];
    var s;
    var n;

    for (s = 0; s < 5; s++) {
      for (n = 0; n < counts[s]; n++) stars.push(s + 1);
    }

    // Fisher-Yates first, then dates assigned afterwards, so every slice of the
    // timeline carries a natural mix of ratings instead of blocks of 5s.
    for (var i = stars.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var tmp = stars[i];
      stars[i] = stars[j];
      stars[j] = tmp;
    }

    var now = config.now;
    var span = config.spanDays * 86400000;
    var reviews = new Array(stars.length);

    for (var k = 0; k < stars.length; k++) {
      var rating = stars[k];
      var bucket = rating === 5 ? COPY.great : rating === 4 ? COPY.good : COPY.average;
      var first = pick(rand, FIRST_NAMES);
      var last = pick(rand, LAST_NAMES);

      // Squaring a uniform draw clusters dates toward the recent past, the way
      // a growing store actually accumulates reviews.
      var age = rand();
      age = age * age;

      reviews[k] = {
        id: k,
        rating: rating,
        name: first + ' ' + last.charAt(0) + '.',
        initials: first.charAt(0) + last.charAt(0),
        title: pick(rand, bucket.titles),
        body: pick(rand, bucket.bodies1) + ' ' + pick(rand, bucket.bodies2),
        date: now - age * span,
        verified: rand() > 0.14,
        helpful: Math.floor(Math.pow(rand(), 2.2) * 64),
        photo: config.photos.length && rand() < 0.07
          ? config.photos[Math.floor(rand() * config.photos.length) % config.photos.length]
          : null
      };
    }

    return reviews;
  }

  /* ------------------------------------------------------- real reviews */

  /*
    Shopify's comment form only carries author, email and body, so the rating,
    the product it belongs to, and the title are packed into the body as
    "[5/5|product-handle] Title\n\nBody".

    Carrying the product handle is what lets one blog article back every SKU in
    the store: each product page keeps only the comments stamped with its own
    handle. A per-product article is still supported and preferred at scale --
    then the handle segment is just redundant.

    The handle group is optional, so a comment posted before this (or with JS
    off) still renders, just without stars.
  */
  var ENCODED = /^\s*\[([1-5])\/5(?:\|([A-Za-z0-9._-]+))?\][ \t]*([^\n]*)\n*([\s\S]*)$/;

  function encodeBody(rating, title, body, productHandle) {
    var stamp = productHandle ? '/5|' + productHandle : '/5';
    return '[' + rating + stamp + '] ' + (title || '').trim() + '\n\n' + (body || '').trim();
  }

  /*
    Shopify hands back comment.content as formatted HTML -- each paragraph
    wrapped in <p> -- not the text that was submitted. Block tags become the
    newlines the encoding relies on, everything else is dropped, then entities
    are decoded (&amp; last, so "&amp;lt;" does not turn into "<").
  */
  var ENTITIES = { '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };

  function stripHtml(html) {
    return String(html)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<\/?(?:p|div|li|h[1-6])[^>]*>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;|&gt;|&quot;|&#39;|&nbsp;/g, function (e) {
        return ENTITIES[e];
      })
      .replace(/&amp;/g, '&')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  function decodeComment(raw, index) {
    var text = stripHtml(raw.content || '');
    var match = ENCODED.exec(text);
    var name = (raw.author || 'Anonymous').trim();
    var parts = name.split(/\s+/);
    var initials = (parts[0] || '?').charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '');

    return {
      id: 'real-' + index,
      rating: match ? parseInt(match[1], 10) : 0,
      product: match ? match[2] || '' : '',
      name: name,
      initials: initials.toUpperCase(),
      title: match ? match[3] : '',
      body: match ? match[4] : text,
      date: raw.date,
      verified: false,
      real: true,
      pending: !!raw.pending,
      helpful: 0,
      photo: null
    };
  }

  /* --------------------------------------------------------------- markup */

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatDate(ms) {
    var d = new Date(ms);
    return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  function formatCount(n) {
    return n.toLocaleString('en-IN');
  }

  function starsMarkup(rating, size) {
    var out = '<span class="pr-stars' + (size ? ' pr-stars--' + size : '') + '" aria-hidden="true">';
    for (var i = 1; i <= 5; i++) {
      var fill = rating >= i ? 100 : rating > i - 1 ? Math.round((rating - (i - 1)) * 100) : 0;
      out += '<span class="pr-star" style="--star-fill:' + fill + '%"></span>';
    }
    return out + '</span>';
  }

  var ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return ESCAPES[c];
    });
  }

  // Escape first, then reinstate the author's line breaks as markup.
  function escapeParagraphs(str) {
    return escapeHtml(str)
      .replace(/\n{2,}/g, '<br><br>')
      .replace(/\n/g, '<br>');
  }

  function reviewMarkup(r) {
    var photo = r.photo
      ? '<div class="pr-review__media"><img src="' + escapeHtml(r.photo) +
        '" alt="Customer photo from ' + escapeHtml(r.name) + '" loading="lazy" width="140" height="140"></div>'
      : '';

    var verified = r.verified
      ? '<span class="pr-verified"><svg viewBox="0 0 16 16" aria-hidden="true">' +
        '<path d="M8 0.8 9.9 2.5 12.4 2.2 13.1 4.6 15.2 6 14.2 8.2 15.2 10.4 13.1 11.8 12.4 14.2 9.9 13.9 8 15.6 6.1 13.9 3.6 14.2 2.9 11.8 0.8 10.4 1.8 8.2 0.8 6 2.9 4.6 3.6 2.2 6.1 2.5Z"/>' +
        '<path class="pr-verified__tick" d="M7 10.6 4.6 8.2l1-1L7 8.6l3.4-3.4 1 1z"/></svg>Verified</span>'
      : '';

    if (r.pending) {
      verified += '<span class="pr-pending">Awaiting approval</span>';
    }

    // Helpful votes are session-only; real comments have no vote store behind
    // them, so the control is hidden rather than faked.
    var foot = r.real
      ? ''
      : '<div class="pr-review__foot">' +
          '<button type="button" class="pr-helpful" data-id="' + r.id + '">' +
            '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M7.5 18h7.1a2 2 0 0 0 1.95-1.55l1.3-5.6A1.6 1.6 0 0 0 16.3 9h-4.05l.6-3.1a2.1 2.1 0 0 0-2.06-2.5.8.8 0 0 0-.73.47L7.5 9.2zM2 9.5h3.5V18H2z"/></svg>' +
            '<span>Helpful</span> <span class="pr-helpful__count">' + r.helpful + '</span>' +
          '</button>' +
        '</div>';

    return (
      '<article class="pr-review' + (r.real ? ' pr-review--real' : '') + '" data-id="' + r.id + '">' +
        '<div class="pr-review__head">' +
          '<span class="pr-review__avatar" aria-hidden="true">' + escapeHtml(r.initials) + '</span>' +
          '<div class="pr-review__meta">' +
            '<p class="pr-review__name">' + escapeHtml(r.name) + verified + '</p>' +
            '<p class="pr-review__date">' + formatDate(r.date) + '</p>' +
          '</div>' +
          (r.rating > 0 ? starsMarkup(r.rating, 'sm') : '') +
        '</div>' +
        (r.title ? '<h3 class="pr-review__title">' + escapeHtml(r.title) + '</h3>' : '') +
        '<p class="pr-review__body">' + escapeParagraphs(r.body) + '</p>' +
        photo +
        foot +
      '</article>'
    );
  }

  /* -------------------------------------------------------------- element */

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      var self = this;

      /*
        Attached before anything can bail out. A native comment POST redirects
        to the blog article, so if setup below fails the customer must still not
        be thrown off the product page.
      */
      this.addEventListener('submit', function (event) {
        var f = event.target;
        if (!f || f.tagName !== 'FORM') return;
        // A form with a target posts into that frame and cannot navigate this
        // page, so it is allowed through -- that is how the review form gets
        // Shopify's CAPTCHA token attached. Anything else is cancelled.
        if (!f.getAttribute('target')) event.preventDefault();
      }, true);

      /*
        Diagnostic marker. If a submit still navigates away to the blog article,
        check this attribute in DevTools: absent means this script never ran (a
        cached or stale asset), present means it ran and the problem is
        elsewhere. Cheap to keep, and it makes "is the new build live?"
        answerable in one line.
      */
      this.setAttribute('data-pr-ready', PR_BUILD);

      var configEl = this.querySelector('[data-reviews-config]');
      if (!configEl) return;

      this.config = JSON.parse(configEl.textContent);
      this.config.now = Date.now();

      // Real, approved comments sit ahead of the generated demo rows. They are
      // ordinary review objects from here on, so they take part in sorting,
      // filtering and search like everything else.
      this.real = (this.config.real || []).map(decodeComment);

      /*
        Off by default: every review shows on every SKU, whichever product it
        was written for. Turning the section setting on narrows each page to
        the comments stamped with its own handle -- unstamped ones are dropped
        then, since there is no way to tell which product they belong to.
      */
      if (this.config.filterByProduct) {
        var handle = this.config.productHandle;
        this.real = this.real.filter(function (r) {
          return r.product === handle;
        });
      }

      this.all = this.real.concat(buildReviews(this.config));
      this.voted = {};

      // Generated ids are indexes into the generated array, so once real
      // reviews are prepended an id is no longer a position in this.all.
      this.byId = {};
      this.all.forEach(function (r) {
        this.byId[r.id] = r;
      }, this);

      this.list = this.querySelector('[data-reviews-list]');
      this.moreBtn = this.querySelector('[data-reviews-more]');
      this.countEl = this.querySelector('[data-reviews-showing]');
      this.emptyEl = this.querySelector('[data-reviews-empty]');
      this.sortEl = this.querySelector('[data-reviews-sort]');
      this.searchEl = this.querySelector('[data-reviews-search]');
      this.clearEl = this.querySelector('[data-reviews-clear]');
      this.filterLabel = this.querySelector('[data-reviews-filter-label]');

      this.starFilter = 0;
      this.query = '';
      this.perPage = this.config.perPage || 8;
      this.shown = this.perPage;

      this.bind();
      this.updateSummary();
      this.render();
    }

    /*
      Liquid computes the summary from the section settings alone, so it does
      not know about approved comments or one just submitted. Recomputing from
      this.all keeps the headline number, the average and the histogram honest
      -- and it also formats the counts, which Liquid cannot do (no
      thousands-separator filter).
    */
    updateSummary() {
      var rows = this.all;
      var total = rows.length;
      var buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      var sum = 0;
      var rated = 0;

      rows.forEach(function (r) {
        if (!r.rating) return; // an unrated legacy comment skews nothing
        buckets[r.rating] += 1;
        sum += r.rating;
        rated += 1;
      });

      var average = rated ? sum / rated : 0;
      var peak = Math.max(buckets[1], buckets[2], buckets[3], buckets[4], buckets[5]) || 1;

      var totalEl = this.querySelector('[data-reviews-total]');
      if (totalEl) totalEl.textContent = formatCount(total);

      var avgEl = this.querySelector('[data-reviews-average]');
      if (avgEl) avgEl.textContent = (Math.round(average * 100) / 100).toString();

      var starsEl = this.querySelector('[data-reviews-average-stars]');
      if (starsEl) {
        starsEl.innerHTML = starsMarkup(average, 'lg').replace(/^<span[^>]*>|<\/span>$/g, '');
      }

      this.querySelectorAll('[data-star-filter]').forEach(function (row) {
        var value = parseInt(row.getAttribute('data-star-filter'), 10);
        var count = buckets[value] || 0;
        var countEl = row.querySelector('.pr-bar__count');
        var fillEl = row.querySelector('.pr-bar__fill');
        if (countEl) countEl.textContent = formatCount(count);
        if (fillEl) fillEl.style.setProperty('--pr-bar-width', (count * 100 / peak).toFixed(1) + '%');
        row.setAttribute('aria-label', 'Show only ' + value + ' star reviews (' + count + ')');
      });
    }

    bind() {
      var self = this;

      if (this.sortEl) {
        this.sortEl.addEventListener('change', function () {
          self.shown = self.perPage;
          self.render();
        });
      }

      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', function () {
          self.shown += self.perPage;
          self.render(true);
        });
      }

      if (this.searchEl) {
        var timer;
        this.searchEl.addEventListener('input', function () {
          clearTimeout(timer);
          timer = setTimeout(function () {
            self.query = self.searchEl.value.trim().toLowerCase();
            self.shown = self.perPage;
            self.render();
          }, 180);
        });
      }

      this.querySelectorAll('[data-star-filter]').forEach(function (row) {
        row.addEventListener('click', function () {
          var value = parseInt(row.getAttribute('data-star-filter'), 10);
          self.starFilter = self.starFilter === value ? 0 : value;
          self.shown = self.perPage;
          self.render();
          self.scrollToList();
        });
      });

      if (this.clearEl) {
        this.clearEl.addEventListener('click', function () {
          self.starFilter = 0;
          self.query = '';
          if (self.searchEl) self.searchEl.value = '';
          self.shown = self.perPage;
          self.render();
        });
      }

      // Delegated, so it keeps working across re-renders.
      this.list.addEventListener('click', function (event) {
        var btn = event.target.closest('.pr-helpful');
        if (!btn) return;
        var id = btn.getAttribute('data-id');
        if (self.voted[id]) return;
        var review = self.byId[id];
        if (!review) return;
        self.voted[id] = true;
        review.helpful += 1;
        btn.classList.add('is-voted');
        btn.querySelector('.pr-helpful__count').textContent = review.helpful;
      });

      var writeBtn = this.querySelector('[data-reviews-write]');
      /*
        Prefer the real <form>. The data attribute is passed through Shopify's
        {% form %} tag, and if it ever fails to render, falling back to the
        element itself keeps the submit intercepted rather than letting the
        browser post it and navigate away.
      */
      var form =
        this.querySelector('form.pr-form') ||
        this.querySelector('[data-reviews-form]') ||
        this.querySelector('form');
      if (writeBtn && form) {
        writeBtn.addEventListener('click', function () {
          if (form.hasAttribute('hidden')) {
            form.removeAttribute('hidden');
            writeBtn.setAttribute('aria-expanded', 'true');
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            form.setAttribute('hidden', '');
            writeBtn.setAttribute('aria-expanded', 'false');
          }
        });

      }

      /*
        Submit is delegated to the section rather than bound to one element.
        submit events bubble, so this catches the review form however it was
        found -- and guarantees no form inside this section can post natively
        and navigate the customer away to the blog article.
      */
      /*
        The Submit button is type="button", so this click is the only path that
        actually posts. Delegated, so it survives the form being re-rendered.
      */
      this.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-reviews-submit]');
        if (!btn) return;
        var owner = btn.closest('form') || form;
        if (!owner) return;
        event.preventDefault();
        self.submit(null, owner);
      });

      /*
        Backstop for implicit submission: pressing Enter in a text field can
        still fire a submit event in some browsers even with no submit button.
      */
      this.addEventListener('submit', function (event) {
        var target = event.target;
        if (!target || target.tagName !== 'FORM') return;

        // Our own requestSubmit() re-entry: let it post to the iframe.
        if (self.submitting) {
          self.submitting = false;
          return;
        }

        self.submit(event, target);
      });

      /*
        The hidden iframe loading means Shopify has answered the post. There is
        no other completion signal, since the response never reaches this page.
      */
      var sink = this.querySelector('[data-reviews-sink]');
      if (sink) {
        sink.addEventListener('load', function () {
          // Ignore the empty load a fresh iframe can fire on its own.
          if (!self.awaitingSink) return;
          self.awaitingSink = false;
          self.onSubmitted(sink);
        });
      }
    }

    /*
      Shopify's comment endpoint answers a normal POST with a redirect to the
      blog article, which would drop the customer off the product page. Posting
      the same form with fetch keeps them here; if fetch is unavailable or the
      request fails we fall through to the native submit so the review is never
      silently lost.
    */
    submit(event, form) {
      var self = this;

      /*
        Reached two ways: the Submit button's click (the normal path -- that
        button is type="button", so it never posts natively), and the submit
        event, which only fires if a browser does implicit submission on Enter.
        Cancel that immediately; Shopify answers a comment POST with a redirect
        to the blog article.
      */
      if (event && event.preventDefault) event.preventDefault();

      // Native validation still applies even though nothing posts natively.
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      /*
        Remember where we are before posting. If the fetch path ever fails and
        the browser posts natively, Shopify redirects to the blog article --
        templates/article.reviews.liquid reads this and sends them back here.
      */
      try {
        sessionStorage.setItem('pr-return-url', window.location.href);
      } catch (e) {
        // Private browsing can throw on write; the article template falls back
        // to document.referrer.
      }

      var bodyEl = form.querySelector('[data-reviews-body]') || form.querySelector('textarea');
      var titleEl = form.querySelector('[data-reviews-title]');
      var checked = form.querySelector('[data-reviews-rating] input:checked');
      var note = form.querySelector('[data-reviews-form-note]');

      var say = function (message, isError) {
        if (!note) return;
        note.textContent = message;
        // The note is styled as a confirmation by default; failures must not
        // be delivered in success green.
        note.classList.toggle('pr-form__note--error', !!isError);
        note.hidden = false;
      };

      if (!bodyEl) {
        say('Sorry, the review form is not set up correctly.', true);
        return;
      }

      var rating = checked ? parseInt(checked.value, 10) : 5;
      var title = titleEl ? titleEl.value : '';
      var body = bodyEl.value;

      if (!body.trim()) {
        say('Please write your review before submitting.', true);
        bodyEl.focus();
        return;
      }

      // Pack rating, product and title into the one field Shopify will carry.
      bodyEl.value = encodeBody(rating, title, body, self.config.productHandle);

      /*
        Handed to the NATIVE submit on purpose, not to fetch.

        Shopify guards comment forms with spam protection, and its script hooks
        the native submit to fetch a CAPTCHA token and inject it as a hidden
        field. A fetch() POST bypasses that entirely and Shopify answers
        "400 Missing CAPTCHA token" -- there is no header or field we can add
        ourselves, because the token has to be minted by that script.

        So the form posts natively, and its target attribute points at a hidden
        iframe. The token gets attached, Shopify's redirect lands in the iframe,
        and the page the customer is on never navigates.
      */
      var sink = self.querySelector('[data-reviews-sink]');
      var submitBtn = form.querySelector('[data-reviews-submit]') || form.querySelector('[type="submit"]');

      if (!sink || !form.getAttribute('target')) {
        say('Sorry, that could not be submitted. Please try again.', true);
        bodyEl.value = body;
        return;
      }

      if (submitBtn) submitBtn.disabled = true;

      // Held so the review can be rendered optimistically once the iframe
      // reports back, by which point the form has already been reset.
      var authorEl = form.querySelector('[name="comment[author]"]');
      self.pending = {
        rating: rating,
        title: title,
        body: body,
        author: authorEl ? authorEl.value : 'You',
        form: form,
        bodyEl: bodyEl,
        submitBtn: submitBtn,
        say: say
      };

      self.awaitingSink = true;
      clearTimeout(self.sinkTimer);
      self.sinkTimer = setTimeout(function () {
        if (!self.awaitingSink) return;
        self.awaitingSink = false;
        say('Sorry, that timed out. Please try again.', true);
        if (submitBtn) submitBtn.disabled = false;
      }, 20000);

      /*
        requestSubmit, not submit: form.submit() skips the submit event, and
        that event is exactly what Shopify's spam-protection script listens for.
        The re-entry guard lets our own re-submit through the handler below.
      */
      self.submitting = true;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        self.submitting = false;
        form.submit();
      }
    }

    /* Called when the hidden iframe has loaded Shopify's response. */
    onSubmitted(sink) {
      var self = this;
      var ctx = this.pending;
      if (!ctx) return;
      this.pending = null;

      clearTimeout(this.sinkTimer);
      if (ctx.submitBtn) ctx.submitBtn.disabled = false;

      /*
        The iframe is same-origin, so the response can be read. Shopify answers
        a rejected comment with an error page rather than a redirect, and that
        is the only way to tell success from failure here.
      */
      var failure = '';
      try {
        var doc = sink.contentDocument;
        if (doc && doc.body) {
          var text = (doc.title || '') + ' ' + doc.body.textContent.slice(0, 600);
          if (/captcha/i.test(text)) {
            failure = 'Spam protection blocked this submission. Please try again.';
          } else if (/(429|too many requests)/i.test(text)) {
            failure = 'Too many reviews submitted just now. Please wait a moment.';
          }
          if (failure && window.console) console.error('[product-reviews] ' + text.slice(0, 300));
        }
      } catch (e) {
        // Cross-origin means Shopify redirected away, which is the success path.
      }

      if (failure) {
        ctx.say(failure, true);
        ctx.bodyEl.value = ctx.body;
        return;
      }

      var pending = self.config.moderated !== false;
      ctx.say(
        pending
          ? 'Thank you for your review. It will appear on this page once it has been approved.'
          : 'Thank you for your review. It has been published.'
      );

      try {
        var fresh = decodeComment({
          author: ctx.author,
          content: encodeBody(ctx.rating, ctx.title, ctx.body, self.config.productHandle),
          date: Date.now(),
          pending: pending
        }, 'new-' + self.real.length);

        self.real.unshift(fresh);
        self.all.unshift(fresh);
        self.byId[fresh.id] = fresh;

        ctx.form.reset();
        ctx.bodyEl.value = '';

        self.shown = Math.max(self.shown, self.perPage);
        self.updateSummary();
        self.render();
      } catch (uiError) {
        if (window.console) console.error('Review posted, but rendering it failed:', uiError);
      }
    }

    scrollToList() {
      var top = this.list.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    filtered() {
      var self = this;
      var rows = this.all;

      if (this.starFilter) {
        rows = rows.filter(function (r) {
          return r.rating === self.starFilter;
        });
      }

      if (this.query) {
        rows = rows.filter(function (r) {
          return (r.title + ' ' + r.body + ' ' + r.name).toLowerCase().indexOf(self.query) !== -1;
        });
      }

      var sort = this.sortEl ? this.sortEl.value : 'recent';
      var sorted = rows.slice();

      if (sort === 'recent') {
        sorted.sort(function (a, b) { return b.date - a.date; });
      } else if (sort === 'oldest') {
        sorted.sort(function (a, b) { return a.date - b.date; });
      } else if (sort === 'highest') {
        sorted.sort(function (a, b) { return b.rating - a.rating || b.date - a.date; });
      } else if (sort === 'lowest') {
        sorted.sort(function (a, b) { return a.rating - b.rating || b.date - a.date; });
      } else if (sort === 'helpful') {
        sorted.sort(function (a, b) { return b.helpful - a.helpful || b.date - a.date; });
      } else if (sort === 'photos') {
        sorted = sorted.filter(function (r) { return r.photo; });
        sorted.sort(function (a, b) { return b.date - a.date; });
      }

      return sorted;
    }

    render(append) {
      var rows = this.filtered();
      var slice = rows.slice(0, this.shown);
      var i;

      if (append) {
        var start = this.list.children.length;
        var html = '';
        for (i = start; i < slice.length; i++) html += reviewMarkup(slice[i]);
        this.list.insertAdjacentHTML('beforeend', html);
        // Only the freshly appended cards animate in.
        for (i = start; i < this.list.children.length; i++) {
          this.list.children[i].classList.add('pr-review--enter');
        }
      } else {
        this.list.innerHTML = slice.map(reviewMarkup).join('');
      }

      if (this.countEl) {
        this.countEl.textContent = rows.length
          ? 'Showing ' + formatCount(slice.length) + ' of ' + formatCount(rows.length) + ' reviews'
          : '';
      }

      if (this.emptyEl) this.emptyEl.hidden = rows.length !== 0;
      if (this.moreBtn) this.moreBtn.hidden = slice.length >= rows.length;

      if (this.filterLabel) {
        var bits = [];
        if (this.starFilter) bits.push(this.starFilter + ' star');
        if (this.query) bits.push('"' + this.query + '"');
        this.filterLabel.textContent = bits.length ? 'Filtered by ' + bits.join(' + ') : '';
      }
      if (this.clearEl) this.clearEl.hidden = !this.starFilter && !this.query;

      var active = this.starFilter;
      this.querySelectorAll('[data-star-filter]').forEach(function (row) {
        var value = parseInt(row.getAttribute('data-star-filter'), 10);
        row.classList.toggle('is-active', value === active);
        row.setAttribute('aria-pressed', value === active ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('product-reviews')) {
    customElements.define('product-reviews', ProductReviews);
  }
})();
